import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang, categoryLabels } from "@/i18n";
import { ProductCard } from "@/components/ProductCard";
import { Reviews } from "@/components/Reviews";
import { useSEO } from "@/lib/useSEO";
import { Truck, ShieldCheck, BadgeCheck } from "lucide-react";

const heroImg = "https://static.prod-images.emergentagent.com/jobs/9449b87b-68e6-4395-9435-2a889042c42d/images/5d271afff363b553df0df42871ff40925afa97ca4f76f1f5b2cc9b7b39439c21.png";
const categoryImgs = {
  rings: "https://images.unsplash.com/photo-1633934542430-0905ccb5f050",
  necklaces: "https://images.unsplash.com/photo-1585960622850-ed33c41d6418",
  bracelets: "https://images.unsplash.com/photo-1633810542706-90e5ff7557be",
  earrings: "https://images.unsplash.com/photo-1692521248622-98a1da77b673",
};

export default function Home() {
  const { t, lang } = useLang();
  const [featured, setFeatured] = useState([]);

  useSEO({ title: "Mimi Silver — Gioielleria Fine | Fine Jewelry", description: t("hero_sub") });

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((res) => setFeatured(res.data.slice(0, 4)));
  }, []);

  return (
    <div className="fade-up">
      {/* Hero */}
      <section className="relative h-[78vh] min-h-[520px] w-full overflow-hidden">
        <img src={heroImg} alt="Mimi Silver" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative h-full px-4 sm:px-8 lg:px-16 flex flex-col justify-center max-w-3xl">
          <span className="text-xs tracking-luxe uppercase text-white/90 mb-4">{t("hero_eyebrow")}</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none mb-6">{t("hero_title")}</h1>
          <p className="text-base text-white/85 max-w-lg leading-relaxed mb-8">{t("hero_sub")}</p>
          <Link
            to="/shop"
            data-testid="hero-cta"
            className="self-start bg-white text-[#1A1F2C] text-xs tracking-luxe uppercase px-10 py-4 hover:bg-[#C5A880] hover:text-white transition-colors duration-300"
          >
            {t("hero_cta")}
          </Link>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-[#EAE6E1] bg-white">
        <div className="px-4 sm:px-8 lg:px-16 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
          {[
            { icon: Truck, label: t("free_shipping") },
            { icon: ShieldCheck, label: t("secure_payment") },
            { icon: BadgeCheck, label: t("authentic") },
          ].map((x, i) => (
            <div key={i} className="flex items-center justify-center gap-3">
              <x.icon className="w-5 h-5 text-[#C5A880]" strokeWidth={1.4} />
              <span className="text-xs tracking-luxe uppercase text-[#1A1F2C]">{x.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A1F2C] text-center mb-14">{t("categories_title")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
          {Object.keys(categoryImgs).map((cat) => (
            <Link
              key={cat}
              to={`/shop/${cat}`}
              data-testid={`category-card-${cat}`}
              className="group relative aspect-[3/4] overflow-hidden bg-[#F0EAE1]"
            >
              <img src={categoryImgs[cat]} alt={cat} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
              <span className="absolute bottom-5 left-5 font-serif-display text-2xl text-white">{categoryLabels[cat][lang]}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="px-4 sm:px-8 lg:px-16 pb-24 bg-white pt-20">
        <div className="text-center mb-14">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A1F2C]">{t("featured_title")}</h2>
          <p className="text-sm text-[#6B7280] mt-2">{t("featured_sub")}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      <Reviews />
    </div>
  );
}
